<template>
  <div class="standard-page v-flex">
    <b-card class="update-message-template-content">
      <ValidationObserver
        ref="observer"
        v-slot="{ errors, invalid }"
        class="view-form"
        tag="form"
        @submit.prevent="handleSubmit"
      >
        <b-card-body class="message-template-body">
          <b-row class="justify-content-md-center">
            <b-col cols="8" class="message-template-body-col">
              <b-row>
                <b-col cols="8">
                  <div class="top-whitespace">
                    <div v-if="isSpecial(messageTemplate.type)">
                      <label> {{ displayName(messageTemplate.type) }} </label>
                      <p class="based-on-label">
                        {{ messageTemplate.description }}
                      </p>
                    </div>
                  </div>

                  <label>TEMPLATE NAME</label>
                  <p class="based-on-label">
                    This name will also be used as the subject for automated
                    email messages.
                  </p>
                  <div class="subject-input">
                    <TextInput
                      v-model="messageTemplate.name"
                      class="name-input"
                      name="name"
                      :errors="errors"
                      rules="required"
                      cy="subject"
                      @input="setDraft"
                    />
                  </div>
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <label>MESSAGE</label>
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <p class="based-on-label">
                    Write your message in the box provided below. You may add
                    substitution codes to your template by choosing from the
                    list provided. Click the Preview buttons to see an example
                    of what your customers will see. Please note, a custom
                    payment link will automatically be added to all templates
                    (see preview for example).
                  </p>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="view-togglers">
                  <p class="view-togglers-label">Preview:</p>
                  <router-link
                    :to="{
                      name: 'MessageTemplateEdit',
                      params: { ...$route.params }
                    }"
                  >
                    <b-badge
                      class="switch-badge"
                      :variant="isCurrentRoute('MessageTemplateEdit')"
                    >
                      Edit
                    </b-badge>
                  </router-link>
                  <router-link
                    :to="{
                      name: 'MessageTemplateText',
                      params: { ...$route.params }
                    }"
                  >
                    <b-badge
                      class="switch-badge"
                      :variant="isCurrentRoute('MessageTemplateText')"
                    >
                      Text
                    </b-badge>
                  </router-link>
                  <router-link
                    :to="{
                      name: 'MessageTemplateEmail',
                      params: { ...$route.params }
                    }"
                  >
                    <b-badge
                      class="switch-badge"
                      :variant="isCurrentRoute('MessageTemplateEmail')"
                    >
                      Email
                    </b-badge>
                  </router-link>
                </b-col>
              </b-row>

              <b-row class="router-container">
                <b-col>
                  <router-view
                    :message-template="messageTemplate"
                    @templateBodyDraft="setDraft"
                  />
                </b-col>
              </b-row>

              <b-row v-if="!isSpecial(messageTemplate.type)">
                <b-col>
                  <label>AUTOMATION</label>
                  <div>
                    <b-row v-if="hasCollectorName">
                      <b-col>
                        <div class="collector-name-warning">
                          <p class="text-danger font-weight-bold">
                            ⚠️ Using automation with collector_name is not
                            available. To add automation, please remove
                            <code>{{ collectorNamePlaceholder }}</code> from
                            your template.
                          </p>
                        </div>
                      </b-col>
                    </b-row>
                    <b-row v-else>
                      <p class="based-on-label ml-3">
                        Automation is set up in Groups: pick who gets this
                        template and when.
                      </p>
                    </b-row>

                    <!-- BP-1546: automation is moving onto groups; this opens the group editor with this template chosen. -->
                    <b-row v-if="$route.params.messageTemplateID">
                      <b-col class="ml-3 mb-3">
                        <b-button
                          variant="blue"
                          data-cy="add-automated-text"
                          @click="createAutomation"
                        >
                          + Add Automated Text
                        </b-button>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card-body>

        <b-card-footer class="create-footer">
          <b-row class="justify-content-md-center">
            <b-col md="6" sm="8">
              <p>
                By clicking save, you confirm that you have received verbal
                consent to send text messages and emails to your customers.
              </p>
            </b-col>
            <b-col class="cancel" md="1" sm="2">
              <a @click="handleCancel"><span>Cancel</span></a>
            </b-col>
            <b-col md="1" sm="2">
              <b-button
                :disabled="isBusy || invalid || isSaveDisabled"
                class="save"
                type="submit"
                data-cy="template-submit-button"
              >
                <div v-if="!isBusy">Save</div>
                <b-spinner v-else small />
              </b-button>
            </b-col>
          </b-row>
        </b-card-footer>
      </ValidationObserver>
    </b-card>
  </div>
</template>

<script lang="ts">
import TextInput from '@/components/FormInputs/TextInput/TextInput.vue'
import { DiscardDraftConfirmation } from '@/mixins/ModalMixins/ModalMixins'
import endpoints from '@/utils/endpoints.js'
import { ValidationObserver } from 'vee-validate'
import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'

Component.registerHooks(['beforeRouteLeave'])

interface MessageTemplate {
  pkid?: number
  email_body: string
  name: string
  sms_body: string
  type: string
  description?: string
}

@Component({
  components: {
    TextInput
  }
})
export default class UpdateMessageTemplate extends mixins(
  DiscardDraftConfirmation
) {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>
  }

  messageTemplate: MessageTemplate = {
    email_body: '',
    name: '',
    sms_body: '',
    type: ''
  }
  hasCollectorNameFlag = false
  recurrence_date = ''
  responseErrors = null
  isBusy = false
  isDraft = false

  async beforeRouteLeave(to, from, next) {
    if (this.isDraft) {
      const response = await this.discardDraftConfirmation()
      if (!response) return
    }
    next()
  }

  // `template` is the template pkid. This page's route param stays the UUID Django uses.
  createAutomation() {
    const templatePkid = this.messageTemplate.pkid
    const template =
      typeof templatePkid === 'number' &&
      Number.isSafeInteger(templatePkid) &&
      templatePkid > 0
        ? String(templatePkid)
        : undefined

    this.$router.push({
      name: 'GroupList',
      params: { merchantID: this.$route.params.merchantID },
      query: { section: 'edit', template }
    })
  }

  mounted() {
    this.setNav()
    if (this.$route.params.messageTemplateID) this.getMessageTemplate()
  }

  displayName(type) {
    const names = {
      AUTOPAY: 'Autopay',
      AUTOPAY_DISABLED: 'Autopay Disabled',
      AUTOPAY_MERCHANT: 'Autopay By Merchant',
      AUTOPAY_DISABLED_MERCHANT: 'Autopay Disabled By Merchant',
      PAYMENT_FAILED: 'Payment Failed',
      PAYMENT_PROMISE: 'Confirm Payment',
      PAYMENT_PROMISE_CONFIRMED: 'Payment Arrangement Confirmation',
      PAYMENT_SCHEDULED: 'Payment Scheduled',
      PAYMENTREQUEST: 'Payment Request',
      THANKS: 'Thank You',
      WELCOME: 'Welcome',
      REGISTER: 'Register',
      UNCONFIRMED_PROMISE: 'Unconfirmed Promise Reminder',
      UNCONFIRMED_PAYMENT_METHOD: 'Unconfirmed Payment Method'
    }

    return names[type]?.toUpperCase()
  }

  getMessageTemplate() {
    this.axios
      .get(
        endpoints.retrieveMessageTemplate(
          this.$route.params.merchantID,
          this.$route.params.messageTemplateID
        )
      )
      .then(res => {
        const english_text_body = res.data.message_bodies.filter(
          e => e.language === 'en-US' && e.body_type === 'TEXT'
        )
        delete res.data.message_bodies
        this.messageTemplate = res.data
        this.messageTemplate.sms_body = english_text_body[0]?.message_body
      })
  }

  handleCancel() {
    this.$router.push({
      name: 'MessageTemplateList',
      params: { ...this.$route.params }
    })
  }

  async handleSubmit() {
    let response: any = {}
    this.isBusy = true
    const requestType = this.$route.params.messageTemplateID ? 'PUT' : 'POST'
    const payload = {
      name: this.messageTemplate.name,
      message_bodies: [
        {
          language: 'en-US',
          body_type: 'TEXT',
          message_body: this.messageTemplate.sms_body
        },
        {
          language: 'en-US',
          body_type: 'EMAIL',
          message_body: this.messageTemplate.sms_body
        }
      ]
    }

    // Groups own automation now, so a save keeps the template's type: re-saving a SCHEDULED reminder must
    // not turn it UNSCHEDULED, which drops the payment link its sends append. New templates start UNSCHEDULED.
    // delta_days is left out, so Django keeps it, except a collector_name template can never be automated.
    if (this.isSpecial(this.messageTemplate.type)) {
      payload['type'] = this.messageTemplate.type
    } else if (this.hasCollectorName) {
      payload['type'] = 'UNSCHEDULED'
      payload['delta_days'] = null
    } else {
      payload['type'] = this.messageTemplate.type || 'UNSCHEDULED'
    }

    this.isDraft = false
    try {
      response = await this.axios({
        method: requestType,
        url: this.messageTemplateSubmitTo,
        data: payload
      })

      this.$toasted.show('Template saved!', {
        duration: 2000,
        position: 'bottom-center'
      })

      if (!this.$route.params.messageTemplateID) {
        this.$router.push({
          name: 'MessageTemplateEdit',
          params: { messageTemplateID: response.data.id }
        })
      }
    } catch (error: any) {
      this.$refs.observer.setErrors(error.response.data)
      this.isDraft = true
    } finally {
      this.isBusy = false
      this.setNav()
    }
  }

  isCurrentRoute(route) {
    return route === this.$route.name ? 'dark' : 'light'
  }

  isSpecial(type) {
    const types = [
      'AUTOPAY',
      'AUTOPAY_DISABLED',
      'AUTOPAY_MERCHANT',
      'AUTOPAY_DISABLED_MERCHANT',
      'PAYMENT_FAILED',
      'PAYMENT_PROMISE',
      'PAYMENT_PROMISE_CONFIRMED',
      'PAYMENT_SCHEDULED',
      'PAYMENTREQUEST',
      'THANKS',
      'WELCOME',
      'REGISTER',
      'UNCONFIRMED_PROMISE',
      'UNCONFIRMED_PAYMENT_METHOD'
    ]

    return !!types.find(t => t === type)
  }

  setDraft() {
    this.isDraft = true
    this.$refs.observer.reset()
    this.checkAndUpdateAutomation()
  }

  setNav() {
    const currentNavTitle = this.$route.params.messageTemplateID
      ? 'Edit Template'
      : 'Create Template'

    const navTitle = [
      { crumbTitle: 'Templates', directTo: 'MessageTemplateList' },
      { crumbTitle: currentNavTitle }
    ]

    this.$store.dispatch('setPageTitle', navTitle)
  }

  get isSaveDisabled() {
    return this.messageTemplate.sms_body?.length === 0
  }

  get hasCollectorName() {
    return this.hasCollectorNameFlag
  }

  get collectorNamePlaceholder() {
    return '{{ collector_name }}'
  }

  @Watch('messageTemplate.sms_body')
  onTemplateBodyChange() {
    this.checkAndUpdateAutomation()
  }

  checkAndUpdateAutomation() {
    const hasCollector =
      this.messageTemplate.sms_body?.includes('{{ collector_name }}') || false
    this.hasCollectorNameFlag = hasCollector
  }

  get messageTemplateSubmitTo() {
    return this.$route.params.messageTemplateID
      ? endpoints.updateMessageTemplate(
          this.$route.params.merchantID,
          this.$route.params.messageTemplateID
        )
      : endpoints.createMessageTemplate(this.$route.params.merchantID)
  }
}
</script>

<style lang="scss">
.card-body {
  overflow: auto;
}

.based-on-container .text-danger {
  position: absolute;
}
</style>

<style lang="scss" scoped>
.top-whitespace {
  margin-top: 50px;
}

.update-message-template-content {
  flex: 1;

  color: $table-header-color;

  div.card-body {
    display: flex;
    padding: 0;

    .view-form {
      max-width: none;
    }
  }

  .message-template-body {
    padding: 1.25rem;

    .message-template-body-col {
      height: 75vh;
    }
  }

  .view-form {
    display: flex;
    flex: 1 grow;
    flex-direction: column;
    justify-content: space-between;
  }

  div.row {
    label {
      margin-bottom: 11px;

      font-weight: bold;
      font-size: 20px;
      letter-spacing: 0.2px;
    }

    p {
      margin: 0;

      font-size: 14px;
    }

    select {
      width: 228px;
      height: 31px;
      padding: 7px 16px 8px;

      color: $darker-gray;
      font-size: 14px;
      font-family: $body-font-family;

      border: 1px solid $darker-gray;
      opacity: 1;
    }

    input {
      height: 31px;
      padding: 7px 15px 8px 16px;

      color: $darker-gray;
      font-size: 14px;
      font-family: $body-font-family;

      border: 1px solid $darker-gray;
      opacity: 1;
    }

    span {
      font-weight: bold;
      font-size: 12px;

      border-radius: 10px;
    }

    .based-on-label {
      padding-bottom: 11px;

      font-weight: bold;
      letter-spacing: 0.14px;
    }

    .based-on-container {
      display: flex;
      align-items: center;
      margin-bottom: 23px;
      padding-right: 0;

      .days-container,
      .days-in-relation-select {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .based-on-days-input {
      display: flex;
      width: 40px;
      margin: 0 15px 0 16px;
    }

    .based-on-days-label {
      color: $darker-gray;
      font-size: 14px;
      font-family: $body-font-family;
      letter-spacing: 0;
    }

    .collector-name-warning {
      margin: 15px 0;
      padding: 12px 16px;
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      border-radius: 4px;

      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
      }
    }

    .subject-input {
      width: 228px;
      margin-bottom: 23px;
    }

    .router-container {
      height: 330px;
    }

    .view-togglers {
      display: flex;
      align-items: center;
      margin-top: 30px;

      .view-togglers-label {
        padding-top: 2px;
        padding-right: 10px;

        color: $darker-gray;
        font-size: 14px;
        font-family: $body-font-family;
        letter-spacing: 0;
      }

      .switch-badge {
        margin: 0 0.5px;
        padding: 2px 8px;
      }
    }
  }
}

.create-footer {
  z-index: 2;

  width: 100%;
  height: 70px;
  padding: 0.75rem;

  background-color: $background;
  border-top: 3px solid $lighter-gray;

  p {
    color: gray;
    font-size: 14px;
    letter-spacing: 0;
  }

  button {
    padding: 8px 25px;

    font-size: 16px;

    background-color: $blue;
    border-radius: 4px;
  }

  span {
    color: $dark-blue;
    text-decoration: underline;
  }

  .save {
    width: 90px;
    height: 38px;

    background-color: $button-blue;

    &:hover {
      background-color: $hover-button-blue;
    }
  }

  .cancel {
    display: flex;
    justify-content: flex-end;

    line-height: 40px;

    a:hover {
      cursor: pointer;
    }
  }
}
</style>
